import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { type TradeBenefitsYourFirmMembers_viewer$key } from './__generated__/TradeBenefitsYourFirmMembers_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const MAX_MEMBERS_TO_SHOW = 7;

const viewerFragment = graphql`
    fragment TradeBenefitsYourFirmMembers_viewer on Viewer {
        user(userId: $userId) {
            tradeFirm {
                members {
                    userId
                    profile {
                        displayName
                    }
                }
            }
        }
    }
`;

const TradeBenefitsYourFirmMembers: FC<{
    viewer: TradeBenefitsYourFirmMembers_viewer$key;
}> = ({ viewer: viewerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const members = viewer?.user?.tradeFirm?.members || [];
    const membersToShow = members.slice(0, MAX_MEMBERS_TO_SHOW);
    const showViewMore = members.length > MAX_MEMBERS_TO_SHOW;

    return (
        <div className={dibsCss.mtLarge}>
            <div className={dibsCss.textBodyMediumHeavy}>
                <FormattedMessage
                    id="abt.TradeBenefitsYourFirmMembers.title"
                    defaultMessage="Firm Members"
                />
            </div>
            <div
                className={classnames(
                    dibsCss.mtMedium,
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapXxsmall
                )}
            >
                {membersToShow.map(member => (
                    <div key={member?.userId}>
                        <div className={dibsCss.textBodyMediumNormal}>
                            {member?.profile?.displayName}
                        </div>
                    </div>
                ))}
                {showViewMore && (
                    <Link
                        href="/my/account/professional/"
                        target="_blank"
                        onClick={() =>
                            trackTradeBenefitsEvent({
                                stepInteractionName: 'view more clicked',
                                trigger: 'firm members',
                            })
                        }
                    >
                        <span className={dibsCss.textBodyMediumHeavy}>
                            <FormattedMessage
                                id="abt.TradeBenefitsYourFirmMembers.viewAllMembers"
                                defaultMessage="View More"
                            />
                        </span>
                    </Link>
                )}
            </div>
        </div>
    );
};

export { TradeBenefitsYourFirmMembers };
